/**
 * Every customer-visible string in the Running system section, in one module
 * so the lexicon is greppable.
 *
 *   - The thing is "a running copy of your system", or "the copy". Never
 *     sandbox, snapshot, harness, container, machine id or desk.
 *   - The three states are Working, Hybrid and Could not run, and a state
 *     that is not Working always says why, in the closed cause list's words.
 *   - No em-dashes or en-dashes.
 *   - A value a person provided is never shown, only that it was provided.
 */
import {
  SYSTEM_CAUSE_WORDS,
  type CapabilityKey,
  type ClaimCoverage,
  type ClaimOutcome,
  type ComponentKind,
  type ComponentSource,
  type ComponentState,
  type NeedKind,
  type ProvenanceLine,
  type RepoBuildState,
  type SystemCauseCode,
  type SystemComponent,
  type SystemEnvironmentView,
  type SystemRepositoryView,
  type SystemState,
} from "@yagni/shared";
import { formatRelative } from "../../lib/relativeTime.js";

export const SECTION_TITLE = "Running system";

/**
 * The cause words, from the shared closed list. Two shared phrasings say
 * "sandbox", which customer copy never does; they read here in the copy's
 * own words. Every other cause reads exactly as the shared list says.
 */
const CAUSE_OVERRIDES: Partial<Record<SystemCauseCode, string>> = {
  system_too_large: "the system needs more memory than one running copy holds",
  substrate: "the machine running the copy failed on our side",
};

export function causeWords(code: SystemCauseCode | null | undefined): string {
  if (!code) return "the reason was not recorded";
  return CAUSE_OVERRIDES[code] ?? SYSTEM_CAUSE_WORDS[code] ?? "the reason was not recorded";
}

export const SYSTEM_STATE_WORD: Record<SystemState, string> = {
  working: "Working",
  hybrid: "Hybrid",
  could_not_run: "Could not run",
};

export const COMPONENT_STATE_WORD: Record<ComponentState, string> = {
  not_set_up: "Not set up",
  setting_up: "Setting up",
  needs_you: "Needs you",
  working: "Working",
  remote: "Remote",
  could_not_run: "Could not run",
};

export const COMPONENT_KIND_WORD: Record<ComponentKind, string> = {
  deployable: "App",
  service: "Service",
  library: "Library",
  infrastructure: "Infrastructure",
  archived: "Archived",
};

/** Libraries, infrastructure and archived repositories are part of the map but never run on their own. */
export function runsOnItsOwn(kind: ComponentKind): boolean {
  return kind === "deployable" || kind === "service";
}

function hostOf(url: string): string {
  try {
    return new URL(url).host || url;
  } catch {
    return url;
  }
}

/** How a part runs, in one line: "Built from acme/web", "From the image mysql:8.4", "Runs remotely at staging.acme.com". */
export function howItRuns(source: ComponentSource): string {
  switch (source.kind) {
    case "build":
      return source.path && source.path !== "."
        ? `Built from ${source.repo} (${source.path})`
        : `Built from ${source.repo}`;
    case "image":
      return `From the image ${source.image}`;
    case "remote":
      return `Runs remotely at ${hostOf(source.url)}`;
  }
}

/** The line under a part's name: why it is not Working, or nothing. */
export function componentCause(
  component: Pick<SystemComponent, "state" | "causeCode">,
): string | null {
  if (component.state === "remote") return `Remote: ${causeWords(component.causeCode)}`;
  if (component.state === "could_not_run")
    return `Could not run: ${causeWords(component.causeCode)}`;
  if (component.state === "needs_you") return `Needs you: ${causeWords(component.causeCode)}`;
  return null;
}

/** The remote edges of a hybrid system, named: every part that is remote or could not run. */
export function remoteEdges(components: readonly SystemComponent[]): SystemComponent[] {
  return components.filter(
    (component) =>
      runsOnItsOwn(component.kind) &&
      (component.state === "remote" || component.state === "could_not_run"),
  );
}

function listWords(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The section's headline: the state, and why when it is not Working. */
export function systemHeadline(
  environment: SystemEnvironmentView | null,
  components: readonly SystemComponent[],
): string {
  if (!environment) {
    return "Not explored yet. Once YAGNI explores this Team's repositories, the running copy of your system shows here.";
  }
  if (environment.activity === "exploring" && environment.state === null) {
    return "Exploring this Team's repositories to bring up a running copy of your system.";
  }
  switch (environment.state) {
    case null:
      return "Not explored yet. Once YAGNI explores this Team's repositories, the running copy of your system shows here.";
    case "working":
      return "Working. Every part of your system runs in the copy.";
    case "hybrid": {
      const edges = remoteEdges(components).map((component) => component.name);
      return edges.length > 0
        ? `Hybrid. Part of your system runs in the copy; ${listWords(edges)} ${edges.length === 1 ? "runs" : "run"} outside it.`
        : `Hybrid. Part of your system runs in the copy: ${causeWords(environment.causeCode)}.`;
    }
    case "could_not_run":
      return `Could not run: ${causeWords(environment.causeCode)}.`;
  }
}

/** What the environment is doing right now, when it is doing something. */
export function activityLine(environment: SystemEnvironmentView | null): string | null {
  if (!environment) return null;
  if (environment.activity === "exploring" && environment.state !== null) {
    return "Exploring again now.";
  }
  if (environment.activity === "refreshing") return "Refreshing the copy now.";
  return null;
}

/** "Refreshed 2h ago", "Explored 3d ago", or nothing before the first Explore. */
export function freshnessLine(
  environment: SystemEnvironmentView | null,
  now: number = Date.now(),
): string | null {
  if (!environment) return null;
  if (environment.lastRefreshedAt)
    return `Refreshed ${formatRelative(environment.lastRefreshedAt, now)}`;
  if (environment.lastExploredAt)
    return `Explored ${formatRelative(environment.lastExploredAt, now)}`;
  return null;
}

/** "About 24 GB" from megabytes, for what the copy needs. */
export function memoryWords(mb: number | null | undefined): string | null {
  if (mb == null || mb <= 0) return null;
  const gb = mb / 1024;
  return gb >= 10 ? `About ${Math.round(gb)} GB` : `About ${gb.toFixed(1).replace(/\.0$/, "")} GB`;
}

export const PROVENANCE_KIND_WORD: Record<ProvenanceLine["kind"], string> = {
  sibling_build: "Built from a sibling repository",
  repair: "Repaired to run",
  generated_secret: "Generated test value",
  stand_in: "Stand-in",
  note: "Note",
};

export const NEED_KIND_WORD: Record<NeedKind, string> = {
  credential: "Credential",
  test_data: "Test data",
  reach: "Access",
  capability: "Not connectable yet",
};

export const CAPABILITY_WORD: Record<CapabilityKey, string> = {
  logs: "logs",
  errors: "error tracking",
  tracker: "the issue tracker",
  ci: "CI",
  feature_flags: "feature flags",
  registry: "a package registry",
  identity_provider: "the sign-in provider",
  queue: "a queue",
  search: "search",
  machine_size: "a larger machine",
  other: "another service",
};

export function capabilityLine(key: CapabilityKey | null): string {
  return `YAGNI cannot connect to ${key ? CAPABILITY_WORD[key] : "this service"} yet. We track it on our side; nothing to do here.`;
}

export const PROVIDED = "Provided";
export const DECLINED = "Declined";
export const PROVIDE_VALUE = "Provide value";
export const PROVIDE_SAVE = "Save";
export const PROVIDE_CANCEL = "Cancel";
export const PROVIDE_SAVED =
  "Saved. The value goes into the next running copy and is never shown again.";
export const PROVIDE_FAILED = "Could not save the value. Try again.";
export const PROVIDE_NOTE =
  "The value is kept in YAGNI's secret store and placed in each running copy after it starts. It is never shown here again.";
export const PROVIDE_ADMIN_ONLY = "An admin can provide this value.";
export const PROVIDE_TOO_SHORT = "A value is at least 8 characters.";
export const MARK_PROVIDED = "Mark provided";
export const MARK_ADMIN_ONLY = "An admin can mark this provided once it is in place.";
export const DECLINE = "Decline";
export const REOPEN = "Reopen";
export const DECLINED_NOTE = "That part stays outside the copy. Reopen it to answer it.";
export const ANSWER_FAILED = "Could not save that answer. Try again.";
export const PROVIDE_PICK_REPO = "Repository it belongs to";

export const NO_NEEDS = "Nothing open. Every value the copy asked for has an answer.";
export const NO_CLAIMS = "No pull request has been tested in the copy yet.";
export const MAP_EMPTY = "No parts mapped yet.";
export const ESTIMATE = "estimate";

export function claimOutcomeWords(
  outcome: ClaimOutcome | null,
  cause: SystemCauseCode | null,
): string {
  switch (outcome) {
    case null:
      return "Running now";
    case "recorded":
      return "Video recorded";
    case "could_not_run":
      return `Could not run: ${causeWords(cause)}`;
    case "cancelled":
      return "Cancelled";
    case "expired":
      return "Ran out of time";
  }
}

/** "2 working, 1 remote": which parts the video covered. */
export function coverageWords(coverage: ClaimCoverage | null): string | null {
  if (!coverage) return null;
  const parts = [`${coverage.working.length} working`];
  if (coverage.remote.length > 0) parts.push(`${coverage.remote.length} remote`);
  if (coverage.couldNotRun.length > 0) parts.push(`${coverage.couldNotRun.length} could not run`);
  return parts.join(", ");
}

export const WATCH_VIDEO = "Watch the video";

// ---------------------------------------------------------------------------
// The System tab: the workspace's System and each repository's build
// ---------------------------------------------------------------------------

export const SYSTEM_TAB_SUB =
  "Each repository your Teams work in, kept as a build proven from a fresh start and rebuilt when its default branch changes";
export const SYSTEM_EMPTY =
  "Nothing explored yet. Once a Team's repositories are explored, each one shows here with its build.";
export const REPOSITORIES_GROUP = "Repositories";
export const SYSTEM_NEEDS_GROUP = "What your system needs";
export const SYSTEM_NEEDS_HINT = "Answer these on Waiting on you.";
export const NO_RECIPE = "Not set up to build yet: explore it first";
export const NOT_BUILT = "Not built yet";

function shortSha(sha: string): string {
  return sha.slice(0, 7);
}

/** What the newest build is doing, when it is not the proven one: building, or did not work. */
function buildInFlightWords(latest: NonNullable<SystemRepositoryView["latest"]>): string | null {
  if (latest.state === "building" || latest.state === "built") {
    return `building ${shortSha(latest.commitSha)} now`;
  }
  if (latest.state === "failed") {
    return `the build at ${shortSha(latest.commitSha)} did not work: ${causeWords(latest.causeCode)}`;
  }
  return null;
}

/** One repository's build in a line: what a pull request would start from, and what is happening now. */
export function repositoryBuildLine(repo: SystemRepositoryView, now: number = Date.now()): string {
  if (!repo.recipe && !repo.latest) return NO_RECIPE;
  const proven = repo.lastProven
    ? `Proven at ${shortSha(repo.lastProven.commitSha)}, ${formatRelative(repo.lastProven.provenAt ?? repo.lastProven.createdAt, now)}`
    : null;
  const latest = repo.latest;
  if (!latest || latest.id === repo.lastProven?.id) return proven ?? NOT_BUILT;
  const current = buildInFlightWords(latest);
  if (!current) return proven ?? NOT_BUILT;
  if (!proven) return current.charAt(0).toUpperCase() + current.slice(1);
  return `${proven}; ${current}${latest.state === "failed" ? ", so the proven build is still used" : ""}`;
}

/** When the repository was last refreshed: its newest build's start. */
export function repositoryRefreshedLine(
  repo: SystemRepositoryView,
  now: number = Date.now(),
): string | null {
  const at = repo.latest?.createdAt;
  return at ? `Refreshed ${formatRelative(at, now)}` : null;
}

export const BUILD_STATE_WORD: Record<RepoBuildState, string> = {
  building: "Building",
  built: "Proving",
  proven: "Proven",
  failed: "Did not build",
  superseded: "Replaced",
};

/** The pill a repository row carries. */
export function repositoryStateWord(repo: SystemRepositoryView): string {
  if (repo.lastProven) return BUILD_STATE_WORD.proven;
  if (repo.latest) return BUILD_STATE_WORD[repo.latest.state];
  return "Not built";
}

export function teamsLine(repo: SystemRepositoryView): string | null {
  if (repo.teams.length === 0) return null;
  return `Used by ${listWords(repo.teams.map((team) => team.name))}`;
}
